import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTokenFromStorage } from '@/utils/auth';
import { buildDiscoveryEmbedSrc } from '@/utils/discoveryEmbed';

/**
 * Full-viewport iframe embedding Discovery at the path that matches the current omni `/creators/*` URL.
 * Users leave the embed via the browser back button or app navigation — no in-UI back control.
 */
export function DiscoveryEmbedPage() {
  const { pathname, search } = useLocation();
  const { t } = useTranslation();
  const token = getTokenFromStorage();

  const src = useMemo(() => {
    if (!token) return '';
    try {
      return buildDiscoveryEmbedSrc(pathname, token, search);
    } catch {
      return '';
    }
  }, [pathname, search, token]);

  if (!token || !src) {
    return (
      <div className="flex min-h-[calc(100vh-3.5rem)] items-center justify-center bg-[var(--background)] p-6">
        <p className="text-sm text-muted-foreground" role="alert">
          {t('discoveryEmbed.missingToken')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)] min-h-[calc(100vh-3.5rem)] w-full flex-col bg-[var(--background)]">
      <iframe
        key={src}
        src={src}
        className="min-h-0 w-full flex-1 border-0"
        title={t('discoveryEmbed.iframeTitle')}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}
